/*! Copyright (c) 2026, XAPPmedia */

export type ParsedQuery = { [key: string]: string | null | (string | null)[] };

/**
 * Parses a query string into an object.
 *
 * Replaces `query-string@7`'s `parse`. `URLSearchParams` alone is not a
 * drop-in — the behavior below was measured against the real package before
 * removal, and several call sites depend on it:
 *
 * - a leading `?` or `#` is stripped
 * - a repeated key collapses to an **array**, not the first value
 * - a valueless key (`?a`) parses to `null`, while `?a=` parses to `""`
 * - values are percent-decoded and `+` is decoded as a space
 * - an empty input produces an empty object
 *
 * @export
 * @param {string} [input]
 * @returns {ParsedQuery}
 */
export function parse(input?: string): ParsedQuery {
    const result: ParsedQuery = {};

    if (!input) {
        return result;
    }

    const trimmed = input.replace(/^[?#&]/, "");

    if (!trimmed) {
        return result;
    }

    for (const pair of trimmed.split("&")) {
        if (!pair) {
            continue;
        }

        const eq = pair.indexOf("=");
        const rawKey = eq === -1 ? pair : pair.slice(0, eq);
        const key = decodeURIComponent(rawKey.replace(/\+/g, " "));
        // A key with no "=" at all is null; "a=" is an empty string.
        const value = eq === -1 ? null : decodeURIComponent(pair.slice(eq + 1).replace(/\+/g, " "));

        if (!Object.prototype.hasOwnProperty.call(result, key)) {
            result[key] = value;
        } else if (Array.isArray(result[key])) {
            (result[key] as (string | null)[]).push(value);
        } else {
            result[key] = [result[key] as string | null, value];
        }
    }

    return result;
}

/**
 * Serializes an object into a query string.
 *
 * Replaces `query-string@7`'s `stringify`. Note it **sorts keys
 * alphabetically**, omits `undefined`, renders `null` as a bare key with no
 * `=`, repeats a key per array element, and encodes spaces as `%20` rather
 * than `+`. `URLSearchParams` does none of those.
 *
 * @export
 * @param {object} [input]
 * @returns {string}
 */
export function stringify(input?: { [key: string]: unknown }): string {
    if (!input) {
        return "";
    }

    const parts: string[] = [];

    for (const key of Object.keys(input).sort()) {
        const value = input[key];

        if (value === undefined) {
            continue;
        }

        const encodedKey = encodeURIComponent(key);

        if (value === null) {
            parts.push(encodedKey);
        } else if (Array.isArray(value)) {
            for (const item of value) {
                if (item === undefined) {
                    continue;
                }
                parts.push(item === null ? encodedKey : `${encodedKey}=${encodeURIComponent(`${item}`)}`);
            }
        } else {
            parts.push(`${encodedKey}=${encodeURIComponent(`${value}`)}`);
        }
    }

    return parts.join("&");
}

/**
 * Splits a URL into its path and parsed query.
 *
 * Replaces `query-string@7`'s `parseUrl`. A URL with no query yields an empty
 * query object rather than undefined.
 *
 * @export
 * @param {string} [input]
 * @returns {{ url: string; query: ParsedQuery }}
 */
export function parseUrl(input?: string): { url: string; query: ParsedQuery } {
    if (!input) {
        return { url: "", query: {} };
    }

    const index = input.indexOf("?");

    return index === -1
        ? { url: input, query: {} }
        : { url: input.slice(0, index), query: parse(input.slice(index + 1)) };
}

export default { parse, stringify, parseUrl };
